import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLiturgical } from "@/context/LiturgicalContext";
import { LITURGY_LABEL } from "@/lib/api";
import { ACTIVE_SOCIALS, SocialIcon } from "@/lib/socials";
import MegaMenu from "@/components/MegaMenu";

const NAV = [
    { to: "/", label: "Today", testid: "nav-today" },
    { to: "/calendar", label: "Calendar", testid: "nav-calendar" },
    { to: "/prayers", label: "Prayers", testid: "nav-prayers" },
    { to: "/rosary", label: "Rosary", testid: "nav-rosary" },
    { to: "/stations", label: "Stations", testid: "nav-stations" },
    { to: "/companion", label: "The Companion", testid: "nav-companion" },
    { to: "/wall", label: "Prayer Wall", testid: "nav-wall" },
    { to: "/shop", label: "Shop", testid: "nav-shop" },
    { to: "/about", label: "About", testid: "nav-about" },
];

const Nav = () => {
    const { today } = useLiturgical();
    const [open, setOpen] = React.useState(false);
    const loc = useLocation();
    React.useEffect(() => { setOpen(false); }, [loc.pathname]);

    return (
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
                <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
                    <img src="/brand/logo.png" alt="Hearts with HIM" className="w-11 h-11 object-contain" />
                    <div className="leading-none">
                        <div className="font-serif text-xl tracking-tight">Hearts with <span className="italic font-semibold">HIM</span></div>
                        {today && <div className="text-[11px] small-caps text-muted-foreground mt-1">{today.season} · {LITURGY_LABEL[today.color]}</div>}
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1" data-testid="main-nav">
                    <MegaMenu />
                    {NAV.map(n => (
                        <NavLink key={n.to} to={n.to} data-testid={n.testid}
                            className={({isActive}) => `px-3 py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${isActive ? 'accent-liturgy' : 'text-muted-foreground hover:text-foreground'}`}>
                            {n.label}
                        </NavLink>
                    ))}
                    <Link to="/support" data-testid="nav-support" className="ml-2 px-4 py-2 text-sm border border-liturgy accent-liturgy hover:bg-liturgy hover:text-white transition-colors duration-300">
                        Support
                    </Link>
                </nav>

                <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="menu">
                    <div className="w-6 h-0.5 bg-foreground mb-1.5" />
                    <div className="w-6 h-0.5 bg-foreground mb-1.5" />
                    <div className="w-6 h-0.5 bg-foreground" />
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-nav">
                    <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
                        {NAV.map(n => (
                            <NavLink key={n.to} to={n.to} data-testid={`m-${n.testid}`}
                                className={({isActive}) => `px-3 py-3 text-base ${isActive ? 'accent-liturgy' : 'text-muted-foreground'}`}>
                                {n.label}
                            </NavLink>
                        ))}
                        <Link to="/support" data-testid="m-nav-support" className="mt-2 px-3 py-3 text-base border border-liturgy accent-liturgy inline-block">Support</Link>
                    </div>
                </div>
            )}
        </header>
    );
};

const Footer = () => (
    <footer className="mt-24 border-t border-border" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
                <div className="font-serif text-2xl">Hearts with <span className="italic font-semibold">HIM</span></div>
                <p className="mt-3 text-sm text-muted-foreground max-w-md leading-relaxed">
                    A quiet return, each morning, to the day the Church is keeping. Prayer, Scripture, and a Friend who walks with you.
                </p>
                <div className="mt-6 flex items-center flex-wrap gap-3" data-testid="footer-socials">
                    {ACTIVE_SOCIALS().map(s => (
                        <a key={s.key} href={s.url} target="_blank" rel="noreferrer" data-testid={`social-${s.key}`}
                           aria-label={s.label} title={s.label}
                           className="w-10 h-10 border border-border flex items-center justify-center hover:border-liturgy hover:accent-liturgy transition-colors">
                            <SocialIcon social={s} size={16} />
                        </a>
                    ))}
                </div>
            </div>
            <div>
                <div className="small-caps text-xs text-muted-foreground mb-4">The Site</div>
                <ul className="space-y-2 text-sm">
                    <li><Link to="/prayers" className="hover:accent-liturgy">Prayers</Link></li>
                    <li><Link to="/rosary" className="hover:accent-liturgy">Rosary</Link></li>
                    <li><Link to="/stations" className="hover:accent-liturgy">Stations of the Cross</Link></li>
                    <li><Link to="/companion" className="hover:accent-liturgy">The Companion</Link></li>
                    <li><Link to="/wall" className="hover:accent-liturgy">Prayer Wall</Link></li>
                    <li><Link to="/calendar" className="hover:accent-liturgy">Liturgical Calendar</Link></li>
                </ul>
            </div>
            <div>
                <div className="small-caps text-xs text-muted-foreground mb-4">Teach & Learn</div>
                <ul className="space-y-2 text-sm">
                    <li><Link to="/why-we-pray" className="hover:accent-liturgy">Why We Pray</Link></li>
                    <li><Link to="/how-to-pray" className="hover:accent-liturgy">How to Pray</Link></li>
                    <li><Link to="/what-to-pray" className="hover:accent-liturgy">What to Pray</Link></li>
                </ul>
            </div>
            <div>
                <div className="small-caps text-xs text-muted-foreground mb-4">More</div>
                <ul className="space-y-2 text-sm">
                    <li><Link to="/lead-magnets" className="hover:accent-liturgy">Free Gifts</Link></li>
                    <li><Link to="/shop" className="hover:accent-liturgy">Shop</Link></li>
                    <li><Link to="/support" className="hover:accent-liturgy">Support the Mission</Link></li>
                    <li><Link to="/contact" className="hover:accent-liturgy">Contact</Link></li>
                </ul>
            </div>
        </div>
        <div className="border-t border-border py-6 text-center text-xs text-muted-foreground">
            <div className="small-caps">Ad Majorem Dei Gloriam</div>
            <div className="mt-1">© {new Date().getFullYear()} Hearts with Him</div>
        </div>
    </footer>
);

const Layout = ({ children }) => (
    <div className="App min-h-screen flex flex-col relative">
        <Nav />
        <main className="flex-1 relative z-10">{children}</main>
        <Footer />
    </div>
);

export default Layout;
